/**
 * The role views' arithmetic (views 3 to 5: bench strength, succession coverage,
 * critical roles), kept apart from the screens so the views and their downloads
 * count the same way.
 *
 * HR's definitions (9 Oct 2026):
 *  - Bench strength = Ready Now people / approved positions.
 *  - Ready Now means ready to step in now, not in 1 to 3 years. A person is Ready
 *    Now for a role when the room confirmed them as a Ready Now successor for it,
 *    or when their growth plan names it as their next role, Ready now. Next roles
 *    are Role Master titles, so they match a role by its title.
 *  - No risk level on critical roles for now.
 *
 * Filters apply to a role by the role's own attributes. The people counted against
 * a role are whoever is ready for it, wherever they sit today.
 */
import type {
  ReportingPerson,
  ReportingReadiness,
  ReportingRole,
  ReportingSuccessionPlan,
} from '@/services/api/talentReportingService'
import {
  CRITICAL_NO,
  CRITICAL_YES,
  FILTER_FIELDS,
  NOT_RECORDED,
  pct,
  roleOf,
  type FilterKey,
  type TalentFilters,
} from './talentDashboardData'

const fold = (s: string | null | undefined) => (s || '').trim().toLocaleLowerCase('en')

/** One key per role: its Role Master code, else its title. */
export function roleKey(code: string | null | undefined, title: string | null | undefined): string {
  return code ? `rm:${code}` : `title:${fold(title)}`
}

export const keyOfRole = (r: ReportingRole) => roleKey(r.role_code, r.role_title)

/** The value a role carries for one dashboard filter. */
export function roleFilterValue(r: ReportingRole, key: FilterKey): string {
  switch (key) {
    case 'role':
      return r.role_title || ''
    case 'critical':
      return r.critical ? CRITICAL_YES : CRITICAL_NO
    default:
      return (r[key] as string | null) || ''
  }
}

export function applyRoleFilters(roles: ReportingRole[], f: TalentFilters): ReportingRole[] {
  return roles.filter((r) => FILTER_FIELDS.every(({ key }) => !f[key] || roleFilterValue(r, key) === f[key]))
}

/** Role keys by title, so a next role named by its title finds its role. Where two
 *  roles share a title, the one with more positions answers for it. */
export function titleIndex(roles: ReportingRole[]): Map<string, string> {
  const out = new Map<string, string>()
  const size = new Map<string, number>()
  for (const r of roles) {
    const t = fold(r.role_title)
    if (!t) continue
    if (!out.has(t) || (size.get(t) || 0) < r.positions) {
      out.set(t, keyOfRole(r))
      size.set(t, r.positions)
    }
  }
  return out
}

/** The role a succession plan covers: the incumbent's Role Master role, else the
 *  role whose title it carries. */
function planKey(plan: ReportingSuccessionPlan, titles: Map<string, string>): string {
  if (plan.role_code) return roleKey(plan.role_code, null)
  return titles.get(fold(plan.role_title)) || roleKey(null, plan.role_title)
}

export interface RoleSuccessors {
  'ready-now': Set<string>
  'ready-1-year': Set<string>
  'ready-2-plus': Set<string>
  any: Set<string>
}

const RANK: Record<string, number> = { 'ready-now': 0, 'ready-1-year': 1, 'ready-2-plus': 2 }

/** Successors per role, each person counted once, at their best readiness for it. */
export function successorsByRole(
  succession: ReportingSuccessionPlan[],
  roles: ReportingRole[],
): Map<string, RoleSuccessors> {
  const titles = titleIndex(roles)
  const best = new Map<string, Map<string, string>>()
  for (const plan of succession) {
    const key = planKey(plan, titles)
    const people = best.get(key) || new Map<string, string>()
    for (const s of plan.successors) {
      const had = people.get(s.candidate_id)
      if (!had || (RANK[s.readiness] ?? 9) < (RANK[had] ?? 9)) people.set(s.candidate_id, s.readiness)
    }
    best.set(key, people)
  }
  const out = new Map<string, RoleSuccessors>()
  for (const [key, people] of best) {
    const bands: RoleSuccessors = { 'ready-now': new Set(), 'ready-1-year': new Set(), 'ready-2-plus': new Set(), any: new Set() }
    for (const [id, readiness] of people) {
      bands.any.add(id)
      if (readiness in RANK) bands[readiness as ReportingReadiness].add(id)
    }
    out.set(key, bands)
  }
  return out
}

/** Ready Now people per role: confirmed Ready Now successors for it, and people
 *  whose growth plan names it as their next role, Ready now. */
export function readyNowByRole(
  people: ReportingPerson[],
  succession: ReportingSuccessionPlan[],
  roles: ReportingRole[],
): Map<string, Set<string>> {
  const titles = titleIndex(roles)
  const out = new Map<string, Set<string>>()
  const add = (key: string | undefined, id: string) => {
    if (!key) return
    const set = out.get(key) || new Set<string>()
    set.add(id)
    out.set(key, set)
  }
  for (const plan of succession) {
    const key = planKey(plan, titles)
    for (const s of plan.successors) if (s.readiness === 'ready-now') add(key, s.candidate_id)
  }
  for (const p of people) {
    const next = [
      ...(p.next_role ? [{ title: p.next_role, readiness: p.next_role_readiness }] : []),
      ...(p.plan_next_roles || []),
    ]
    for (const n of next) if (n.readiness === 'ready-now') add(titles.get(fold(n.title)), p.employee_id)
  }
  return out
}

export interface Bench {
  roles: number
  positions: number
  filled: number
  vacant: number
  readyNow: number
  /** Ready Now people per approved position, 0 to 100; null with no positions. */
  strength: number | null
}

/** Bench strength over a set of roles: each Ready Now person counted once. */
export function benchOf(roles: ReportingRole[], readyNow: Map<string, Set<string>>): Bench {
  const people = new Set<string>()
  let positions = 0
  let filled = 0
  let vacant = 0
  for (const r of roles) {
    positions += r.positions
    filled += r.filled
    vacant += r.vacant
    for (const id of readyNow.get(keyOfRole(r)) || []) people.add(id)
  }
  return { roles: roles.length, positions, filled, vacant, readyNow: people.size, strength: pct(people.size, positions) }
}

/** Bench strength per group of roles, largest group first, unrecorded last. */
export function benchBy(
  roles: ReportingRole[],
  readyNow: Map<string, Set<string>>,
  pick: (r: ReportingRole) => string | null | undefined,
  order?: string[],
): { label: string; bench: Bench }[] {
  const groups = new Map<string, ReportingRole[]>()
  for (const r of roles) {
    const label = (pick(r) || '').trim() || NOT_RECORDED
    groups.set(label, [...(groups.get(label) || []), r])
  }
  const rank = (label: string) => {
    if (label === NOT_RECORDED) return Number.MAX_SAFE_INTEGER
    if (!order) return 0
    const i = order.indexOf(label)
    return i === -1 ? order.length : i
  }
  return [...groups.entries()]
    .map(([label, rs]) => ({ label, bench: benchOf(rs, readyNow) }))
    .sort((a, b) => rank(a.label) - rank(b.label) || b.bench.positions - a.bench.positions || a.label.localeCompare(b.label))
}

export interface RoleCoverage {
  role: ReportingRole
  key: string
  readyNow: number
  planned: number
  potential: number
  any: number
}

/** Successors per role at each readiness. Shares are of the role's positions
 *  (pct(count, role.positions)); 100% means one successor per position. */
export function coverageRows(roles: ReportingRole[], successors: Map<string, RoleSuccessors>): RoleCoverage[] {
  return roles.map((role) => {
    const key = keyOfRole(role)
    const s = successors.get(key)
    return {
      role,
      key,
      readyNow: s?.['ready-now'].size || 0,
      planned: s?.['ready-1-year'].size || 0,
      potential: s?.['ready-2-plus'].size || 0,
      any: s?.any.size || 0,
    }
  })
}

/** The roles People Leaders hold: successors are named for a People Leader's own
 *  role (HR process notes, 7 Sep 2026), so these are the roles coverage is about. */
export function leaderRoleKeys(
  people: ReportingPerson[],
  succession: ReportingSuccessionPlan[],
  roles: ReportingRole[],
): Set<string> {
  const titles = titleIndex(roles)
  const leaders = new Set(people.map((p) => p.leader_id).filter(Boolean) as string[])
  const out = new Set<string>()
  for (const p of people) {
    if (!leaders.has(p.employee_id)) continue
    out.add(p.role_code ? roleKey(p.role_code, null) : titles.get(fold(roleOf(p))) || roleKey(null, roleOf(p)))
  }
  for (const plan of succession) out.add(planKey(plan, titles))
  return out
}

export type CoverageBand = 'full' | 'partial' | 'none'

/** A role's coverage at any readiness, as the deck bands it. */
export function coverageBand(c: RoleCoverage): CoverageBand {
  const share = pct(c.any, c.role.positions) ?? 0
  return share >= 100 ? 'full' : share > 0 ? 'partial' : 'none'
}

export const COVERAGE_BAND_LABEL: Record<CoverageBand, string> = {
  full: 'A successor for every position',
  partial: 'Some positions covered',
  none: 'No successor named',
}

/** Each filter's choices among the roles the OTHER filters leave, so the filters
 *  also offer what only the role views carry (a role with nobody in it yet). */
export function roleFilterOptions(roles: ReportingRole[], f: TalentFilters): Record<FilterKey, string[]> {
  const out = {} as Record<FilterKey, string[]>
  for (const { key } of FILTER_FIELDS) {
    const values = new Set<string>()
    for (const r of roles) {
      const others = FILTER_FIELDS.every(({ key: k }) => k === key || !f[k] || roleFilterValue(r, k) === f[k])
      if (!others) continue
      const v = roleFilterValue(r, key)
      if (v) values.add(v)
    }
    out[key] = [...values]
  }
  return out
}
